import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { Base, Geometry, Subtraction } from '@react-three/csg'
import WallShaderMaterial from '../../materials/WallShaderMaterial'
import { extend } from '@react-three/fiber'
import { cloneAndScaleUV, setupTextureRepeat } from '../../utils/geometryUltis'
import applyNormalDirectionFilter from '../../utils/normalDirectionFilter'
import { getRoofPerimeterWorld } from "../../utils/roofEdgesUtils"

extend({ WallShaderMaterial })

function ensureBoundingBox(geo) {
  if (geo && !geo.boundingBox) geo.computeBoundingBox()
  return geo
}

function getCenterFromGeo(geo) {
  ensureBoundingBox(geo)
  const c = new THREE.Vector3()
  geo.boundingBox?.getCenter(c)
  return c
}

function unionBoxes(geos) {
  const box = new THREE.Box3()
  geos.forEach((g) => {
    ensureBoundingBox(g)
    if (g?.boundingBox) box.union(g.boundingBox)
  })
  return box
}

export default function GarazSpadWLewo({
                                         gatesData,
                                         doorsData,
                                         windowsData,
                                         textures,
                                         onTrimsData,
                                         ...props
                                       }) {
  const { nodes, materials } = useGLTF('/models/garages/spad-w-lewo.glb')

  const scaledRightGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Right_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Right_side.geometry, props.length, props.height, props.width])

  const scaledLeftGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Left_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100,
      { flipU: true }
    )
    return ensureBoundingBox(g)
  }, [nodes.Left_side.geometry, props.length, props.height, props.width])

  const scaledBackGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Back_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Back_side.geometry, props.length, props.height, props.width])

  const scaledFrontGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Front_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Front_side.geometry, props.length, props.height, props.width])

  const scaledRoofGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Celing.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.width / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Celing.geometry, props.length, props.height, props.width])

  // ===== centrum garażu + kierunki na zewnątrz (object space) =====
  const garageCenter = useMemo(() => {
    const box = unionBoxes([scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])
    const c = new THREE.Vector3()
    box.getCenter(c)
    return c
  }, [scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])

  const dirLeft = useMemo(
    () => getCenterFromGeo(scaledLeftGeometry).sub(garageCenter).normalize(),
    [scaledLeftGeometry, garageCenter]
  )
  const dirRight = useMemo(
    () => getCenterFromGeo(scaledRightGeometry).sub(garageCenter).normalize(),
    [scaledRightGeometry, garageCenter]
  )
  const dirFront = useMemo(
    () => getCenterFromGeo(scaledFrontGeometry).sub(garageCenter).normalize(),
    [scaledFrontGeometry, garageCenter]
  )
  const dirBack = useMemo(
    () => getCenterFromGeo(scaledBackGeometry).sub(garageCenter).normalize(),
    [scaledBackGeometry, garageCenter]
  )

  // ===== windows flatten =====
  const [flattenedWindows, setFlattenedWindows] = useState({})

  const flattenWindows = (data) => {
    const result = {}
    if (!data) return result

    Object.entries(data).forEach(([wallKey, windowTypes]) => {
      const wallWindows = []
      Object.entries(windowTypes || {}).forEach(([typeKey, windows]) => {
        if (!Array.isArray(windows)) return
        windows.forEach((w) => wallWindows.push({ ...w, wall: wallKey, type: typeKey }))
      })
      result[wallKey] = wallWindows
    })

    return result
  }

  useEffect(() => {
    setFlattenedWindows(flattenWindows(windowsData))
  }, [windowsData])

  // ===== subtractions =====
  const subtractionGeometriesFrontWall = useMemo(() => {
    const tmp = []

    if (gatesData?.length) {
      gatesData.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
        })
      })
    }

    if (doorsData?.front_wall?.length) {
      doorsData.front_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [gatesData, doorsData?.front_wall])

  const subtractionGeometriesBackWall = useMemo(() => {
    const tmp = []

    if (doorsData?.back_wall?.length) {
      doorsData.back_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    if (flattenedWindows?.back_wall?.length) {
      flattenedWindows.back_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [doorsData?.back_wall, flattenedWindows?.back_wall])

  const subtractionGeometriesLeftWall = useMemo(() => {
    const tmp = []

    if (doorsData?.left_wall?.length) {
      doorsData.left_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    if (flattenedWindows?.left_wall?.length) {
      flattenedWindows.left_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [doorsData?.left_wall, flattenedWindows?.left_wall])

  const subtractionGeometriesRightWall = useMemo(() => {
    const tmp = []

    if (doorsData?.right_wall?.length) {
      doorsData.right_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    if (flattenedWindows?.right_wall?.length) {
      flattenedWindows.right_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [doorsData?.right_wall, flattenedWindows?.right_wall])

  // ===== materials =====
  const applyRepeat = (tex, rx, ry) => {
    if (!tex) return
    setupTextureRepeat(tex, rx, ry)
  }

  const baseTemplate = useMemo(() => {
    return materials?.Material || materials?.['Material'] || new THREE.MeshStandardMaterial()
  }, [materials])

  const cloneBase = () => baseTemplate.clone()
  const EPS = 0.02

  const makeOuterWallMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_color

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue
      m.roughness = wallColor.roughnessValue
      m.color = new THREE.Color(wallColor.hex)
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'out', EPS)
    m.needsUpdate = true
    return m
  }

  const makeInnerWallMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_inside_color

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue
      m.roughness = wallColor.roughnessValue
      m.color = new THREE.Color(wallColor.hex)
    } else {
      m.color = new THREE.Color('#f5f5f5')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'in', EPS)
    m.needsUpdate = true
    return m
  }

  const leftOuterMat = useMemo(
    () => makeOuterWallMaterial(props.length / 100, 1, dirLeft),
    [baseTemplate, textures?.walls_color, props.length, dirLeft]
  )
  const leftInnerMat = useMemo(
    () => makeInnerWallMaterial(props.length / 100, 1, dirLeft),
    [baseTemplate, textures?.walls_inside_color, props.length, dirLeft]
  )

  const rightOuterMat = useMemo(
    () => makeOuterWallMaterial(props.length / 100, 1, dirRight),
    [baseTemplate, textures?.walls_color, props.length, dirRight]
  )
  const rightInnerMat = useMemo(
    () => makeInnerWallMaterial(props.length / 100, 1, dirRight),
    [baseTemplate, textures?.walls_inside_color, props.length, dirRight]
  )

  const frontOuterMat = useMemo(
    () => makeOuterWallMaterial(1, 1, dirFront),
    [baseTemplate, textures?.walls_color, dirFront]
  )
  const frontInnerMat = useMemo(
    () => makeInnerWallMaterial(1, 1, dirFront),
    [baseTemplate, textures?.walls_inside_color, dirFront]
  )

  const backOuterMat = useMemo(
    () => makeOuterWallMaterial(1, 1, dirBack),
    [baseTemplate, textures?.walls_color, dirBack]
  )
  const backInnerMat = useMemo(
    () => makeInnerWallMaterial(1, 1, dirBack),
    [baseTemplate, textures?.walls_inside_color, dirBack]
  )

  // ==========================================================
  // ROOF: outer/inner jak było
  // ==========================================================
  const roofOuterDir = useMemo(() => {
    const nAttr = scaledRoofGeometry?.attributes?.normal
    let sign = 1

    if (nAttr?.array) {
      const arr = nAttr.array
      let sumY = 0
      for (let i = 1; i < arr.length; i += 3) sumY += arr[i]
      sign = sumY >= 0 ? 1 : -1
    }

    const local = new THREE.Vector3(0, sign, 0)

    const r = nodes?.Celing?.rotation
    if (r) local.applyEuler(r)

    return local.normalize()
  }, [
    scaledRoofGeometry,
    nodes?.Celing?.rotation?.x,
    nodes?.Celing?.rotation?.y,
    nodes?.Celing?.rotation?.z
  ])

  const RoofOuterMaterial = useMemo(() => {
    const m = cloneBase()
    const RoofColor = textures?.roof_color || null

    m.side = THREE.DoubleSide

    if (RoofColor) {
      m.map = RoofColor.textureDiff || null
      m.metalnessMap = RoofColor.textureMetalness || null
      m.normalMap = RoofColor.textureNormalness || null

      applyRepeat(m.map, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.metalnessMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.normalMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))

      m.metalness = RoofColor.metalnessValue ?? 0
      m.roughness = RoofColor.roughnessValue ?? RoofColor.rougnessValue ?? 1
      m.color = new THREE.Color(RoofColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, roofOuterDir, 'out', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, roofOuterDir])

  const RoofInnerMaterial = useMemo(() => {
    const m = cloneBase()
    const RoofColor = textures?.roof_color || null

    m.side = THREE.DoubleSide

    if (RoofColor) {
      m.map = RoofColor.textureDiff || null
      m.metalnessMap = RoofColor.textureMetalness || null
      m.normalMap = RoofColor.textureNormalness || null

      applyRepeat(m.map, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.metalnessMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.normalMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))

      m.metalness = RoofColor.metalnessValue ?? 0
      m.roughness = RoofColor.roughnessValue ?? RoofColor.rougnessValue ?? 1
      m.color = new THREE.Color(RoofColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, roofOuterDir, 'in', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, roofOuterDir])

  const RoofFilcMaterial = useMemo(() => {
    if (Number(props?.moisture_protection) !== 2) return null

    const RoofColor = textures?.roof_color || null
    const filcTex = RoofColor?.texture_filc || null
    if (!filcTex) return null

    const wallsTex = textures?.walls_color || null

    try {
      filcTex.colorSpace = THREE.SRGBColorSpace
      filcTex.needsUpdate = true
    } catch (e) {}

    const m = cloneBase()
    m.side = THREE.DoubleSide

    m.map = filcTex
    applyRepeat(m.map, 1, 1)

    m.normalMap = wallsTex?.textureNormalness || null
    if (m.normalMap) {
      applyRepeat(
        m.normalMap,
        1 / (wallsTex.multiplierHorizontal || 1),
        1 / (wallsTex.multiplierVertical || 1)
      )
    }

    m.metalnessMap = null
    m.metalness = 0
    m.roughness = 1
    m.color = new THREE.Color('#ffffff')

    applyNormalDirectionFilter(m, roofOuterDir, 'in', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, textures?.walls_color, props?.moisture_protection, roofOuterDir])

  const RoofInsideMaterial = RoofFilcMaterial || RoofInnerMaterial

  // ==========================================================
  // ✅ TOOLING: dokładnie jak w "działa idealnie"
  // ==========================================================
  const groupRef = useRef(null)
  const roofOuterRef = useRef(null)

  useLayoutEffect(() => {
    if (!onTrimsData) return
    if (!groupRef.current || !roofOuterRef.current) return

    groupRef.current.updateWorldMatrix(true, true)
    roofOuterRef.current.updateWorldMatrix(true, true)

    const { segments: roofEdgeSegments } = getRoofPerimeterWorld(roofOuterRef.current)

    const wallsBoxLocal = unionBoxes([
      scaledLeftGeometry,
      scaledRightGeometry,
      scaledFrontGeometry,
      scaledBackGeometry,
    ])

    const toWorldXZ = (x, z) => {
      const v = new THREE.Vector3(x, 0, z)
      return groupRef.current.localToWorld(v)
    }

    const wallsBoxWorld = wallsBoxLocal.clone().applyMatrix4(groupRef.current.matrixWorld)
    const groundY = wallsBoxWorld.min.y

    const getRoofYAtXZ = (roofMesh, x, z) => {
      const roofWorldBox = new THREE.Box3().setFromObject(roofMesh)
      const originY = roofWorldBox.max.y + 5

      const raycaster = new THREE.Raycaster()
      raycaster.set(new THREE.Vector3(x, originY, z), new THREE.Vector3(0, -1, 0))

      const hits = raycaster.intersectObject(roofMesh, false)
      if (hits && hits.length) return hits[0].point.y

      return roofWorldBox.max.y
    }

    const minX = wallsBoxLocal.min.x
    const maxX = wallsBoxLocal.max.x
    const minZ = wallsBoxLocal.min.z
    const maxZ = wallsBoxLocal.max.z

    const makeCornerTop = (xLocal, zLocal) => {
      const pWorld = toWorldXZ(xLocal, zLocal)
      const yRoof = getRoofYAtXZ(roofOuterRef.current, pWorld.x, pWorld.z)
      return new THREE.Vector3(pWorld.x, yRoof, pWorld.z)
    }

    const roofCorners = {
      frontLeft: makeCornerTop(minX, minZ),
      frontRight: makeCornerTop(minX, maxZ),
      backLeft: makeCornerTop(maxX, minZ),
      backRight: makeCornerTop(maxX, maxZ),
    }

    const cornerSegments = Object.entries(roofCorners).map(([key, top]) => ({
      key,
      a: new THREE.Vector3(top.x, groundY, top.z),
      b: top.clone(),
    }))

    const cornerList = Object.entries(roofCorners).map(([key, p]) => ({ key, p }))
    cornerList.sort((a, b) => b.p.y - a.p.y)

    const high = cornerList[0]?.p?.clone() || null
    const low = cornerList[cornerList.length - 1]?.p?.clone() || null

    let roofPitchRadians = 0
    let roofSlopeDir = new THREE.Vector3(1, 0, 0)

    if (high && low) {
      const dx = low.x - high.x
      const dz = low.z - high.z
      const run = Math.sqrt(dx * dx + dz * dz)
      const dy = Math.max(0, high.y - low.y)

      roofPitchRadians = Math.atan2(dy, Math.max(1e-6, run))

      const d = new THREE.Vector3(dx, 0, dz)
      if (d.lengthSq() > 1e-12) roofSlopeDir = d.normalize()
    }

    onTrimsData({
      roofEdges: roofEdgeSegments,
      corners: cornerSegments,
      roofCorners,
      groundY,

      roofPitchRadians,
      roofSlopeDir,
      roofHighPoint: high,
      roofLowPoint: low,
      roofHighPoints: high ? [high.clone()] : [],
    })
  }, [
    onTrimsData,
    props.length,
    props.width,
    props.height,
    scaledLeftGeometry,
    scaledRightGeometry,
    scaledFrontGeometry,
    scaledBackGeometry,
  ])

  return (
    <group ref={groupRef} {...props} dispose={null} castShadow receiveShadow>
      {/* ROOF OUTER */}
      <mesh
        ref={roofOuterRef}
        name="Roof_outer"
        geometry={scaledRoofGeometry}
        material={RoofOuterMaterial}
        rotation={nodes.Celing.rotation}
      />

      {/* ROOF INNER */}
      <mesh
        geometry={scaledRoofGeometry}
        material={RoofInsideMaterial}
        rotation={nodes.Celing.rotation}
      />

      {/* FRONT OUTER */}
      <mesh name="Front_side_outer" material={frontOuterMat} rotation={nodes.Front_side.rotation}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* FRONT INNER */}
      <mesh name="Front_side_inner" material={frontInnerMat} rotation={nodes.Front_side.rotation}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT OUTER */}
      <mesh name="Left_side_outer" material={leftOuterMat} rotation={nodes.Left_side.rotation}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT INNER */}
      <mesh name="Left_side_inner" material={leftInnerMat} rotation={nodes.Left_side.rotation}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT OUTER */}
      <mesh name="Right_side_outer" material={rightOuterMat} rotation={nodes.Right_side.rotation}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT INNER */}
      <mesh name="Right_side_inner" material={rightInnerMat} rotation={nodes.Right_side.rotation}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK OUTER */}
      <mesh name="Back_side_outer" material={backOuterMat} rotation={nodes.Back_side.rotation}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK INNER */}
      <mesh name="Back_side_inner" material={backInnerMat} rotation={nodes.Back_side.rotation}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>
    </group>
  )
}
